import { Button, Col, Form, Input, Row, Select, Spin, Switch } from 'antd';
import React, { useEffect, useState } from 'react';
import { ReactSVG } from 'react-svg';
import { ROLES, TYPE_PAGE } from '../../../constants';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { doGetCompanies } from '../../../modules/companies/stores/thunk';
import { doGetLocations } from '../../../modules/locations/stores/thunk';
import { doGetCarriers } from '../../../modules/carriers/stores/thunk';
import { selectAllCarriers } from '../../../modules/carriers/stores/slice';
import { selectAllLocations } from '../../../modules/locations/stores/slice';
import { selectAllCompanies } from '../../../modules/companies/stores/slice';

const initValuesForm = {
  regions: ['Amsterdam'],
  role: ROLES.COMPANY_OWNER,
  isActive: true,
  entityIds: [], // Add this to ensure proper initialization
};

const UserForm = ({ handleCancel, handleOk, user, isModalOpen, isFetching, typePage = TYPE_PAGE.NEW }) => {
  const dispatch = useDispatch();
  const [formUser] = Form.useForm();
  const [selectedRole, setSelectedRole] = useState('');
  const [entityOptions, setEntityOptions] = useState({});

  const companies = useSelector((state) => selectAllCompanies(state));
  const locations = useSelector((state) => selectAllLocations(state));
  const carriers = useSelector((state) => selectAllCarriers(state));

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(doGetCompanies({ page: 1, take: 1000 }));
      await dispatch(doGetLocations({ page: 1, take: 1000 }));
      await dispatch(doGetCarriers());
    };
    fetchData();
  }, []);

  useEffect(() => {
    setEntityOptions({ COMPANY_OWNER: companies, LOCATION_USER: locations, CARRIER_USER: carriers });
  }, [companies, locations, carriers]);

  useEffect(() => {
    if (user) {
      const role = user.role;
      setSelectedRole(role);
      if (role === ROLES.COMPANY_OWNER) {
        formUser.setFieldsValue({ ...user, role, entityIds: user.companyIds });
      } else if (role === ROLES.LOCATION_USER) {
        formUser.setFieldsValue({ ...user, role, entityId: user.locationId });
      } else if (role === ROLES.CARRIER_USER) {
        formUser.setFieldsValue({ ...user, role, entityId: user.carrierId });
      }
    } else {
      formUser.resetFields();
      setSelectedRole('');
    }
  }, [formUser, isModalOpen, user]);

  const onFinishForm = (values) => {
    const params = {
      name: values.name,
      email: values.email,
      role: values.role,
      isActive: values.isActive,
      entityId: values.entityId,
      entityIds: values.entityIds,
    };
    if (values.password) {
      params.password = values.password;
    }

    handleOk && handleOk(params);
  };

  const handleChangeRole = (value) => {
    setSelectedRole(value);
    // Clear both entityId and entityIds when role changes
    formUser.setFieldsValue({ entityId: null, entityIds: null });
  };

  const onCancel = () => {
    handleCancel && handleCancel();
  };

  const onOk = () => {
    formUser.submit();
  };

  return (
    <Spin spinning={isFetching}>
      <div className="UserForm">
        <Form
          form={formUser}
          scrollToFirstError
          layout="vertical"
          requiredMark={false}
          colon={false}
          className="FormContent"
          onFinish={onFinishForm}
          initialValues={initValuesForm}
        >
          <Row gutter={16}>
            <Col span={typePage === TYPE_PAGE.EDIT ? 18 : 24}>
              <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Required' }]}>
                <Input size="large" placeholder="John Doe" />
              </Form.Item>
            </Col>
            {typePage === TYPE_PAGE.EDIT && (
              <Col span={6}>
                <div className="switch-active">
                  <Form.Item name="isActive" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                  <div className="text">
                    <p>Active</p>
                  </div>
                </div>
              </Col>
            )}
            <Col span={24}>
              <Form.Item
                label="Email"
                name="email"
                rules={[{ required: true, type: 'email', message: 'The input is not valid E-mail!' }]}
              >
                <Input
                  disabled={typePage === TYPE_PAGE.EDIT}
                  size="large"
                  placeholder="Email"
                  autoComplete="new-password"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Role" name="role" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  options={Object.keys(ROLES).map((key) => ({ value: ROLES[key], label: key }))}
                  onChange={(value) => handleChangeRole(value)}
                />
              </Form.Item>
            </Col>

            {(() => {
              const currentRole = formUser.getFieldValue('role') || selectedRole;
              if (['LOCATION_USER', 'CARRIER_USER'].includes(currentRole)) {
                return (
                  <Col span={24}>
                    <Form.Item label="" name="entityId">
                      <Select
                        options={entityOptions[currentRole]?.map((key) => ({ value: key.id, label: key.name })) || []}
                        placeholder="Select an entity to connect the user to..."
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                );
              }
              if (['COMPANY_OWNER'].includes(currentRole)) {
                return (
                  <Col span={24}>
                    <Form.Item label="" name="entityIds">
                      <Select
                        mode="multiple"
                        options={entityOptions[currentRole]?.map((key) => ({ value: key.id, label: key.name })) || []}
                        placeholder="Select entities to connect the user to..."
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                );
              }
              return null;
            })()}

            {user?.id && (
              <Col span={24}>
                <div className="ant-form-item ">
                  <label>API key</label>
                  <Input size="large" disabled value={user.apiKey} />
                </div>
              </Col>
            )}
          </Row>
        </Form>
        <div className="FormFooter" style={{ marginTop: 16 }}>
          <Button className="ButtonCancel" onClick={onCancel}>
            Cancel
          </Button>
          <Button className="CreateButton ButtonPrimary" type="primary" onClick={onOk}>
            {user ? 'Edit' : 'Send invite to user'}
            <ReactSVG className="icon" src="/icons/done.svg" />
          </Button>
        </div>
      </div>
    </Spin>
  );
};

export default UserForm;
